/**
 * DbTabList - 通用 Tab 列表组件（性能优化版）
 *
 * 特性：
 * - 支持多 Tab 数据缓存（LRU 算法），避免切换白屏
 * - 自动预加载相邻 Tab 数据
 * - 内置骨架屏、加载更多、下拉刷新
 * - 防抖缓存淘汰，减少频繁操作
 * - 适用于订单列表、商品列表、分类列表等场景
 */
// @ts-nocheck

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import type {
  DbTabListProps,
  TabDataCache,
  TabKey,
  TabItem,
} from './DbTabList.types';
import DbInfiniteList from '../DbInfiniteList';
import DbTab from '../DbTab';
import { DbItemCard } from '../DbItemCard';

// ==================== LRU 缓存实现 ====================

class LRUCache<K, V> {
  private cache: Map<K, V>;
  private maxSize: number;

  constructor(maxSize: number) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // 移动到最新
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // 删除最旧的
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  keys(): IterableIterator<K> {
    return this.cache.keys();
  }

  size(): number {
    return this.cache.size;
  }
}

// ==================== 预加载算法 ====================

interface PreloadConfig {
  currentIndex: number;
  totalCount: number;
  cacheSize: number;
  skipCached?: boolean;
  cachedIndices?: Set<number>;
}

interface PreloadResult {
  indices: number[];
  direction: 'left' | 'right' | 'both';
}

function calculatePreloadIndices(config: PreloadConfig): PreloadResult {
  const { currentIndex, totalCount, cacheSize, skipCached = true, cachedIndices = new Set() } = config;

  const indices: number[] = [];

  // 向左预加载
  for (let i = 1; i <= cacheSize; i++) {
    const leftIndex = currentIndex - i;
    if (leftIndex >= 0) {
      if (!skipCached || !cachedIndices.has(leftIndex)) {
        indices.push(leftIndex);
      }
    }
  }

  // 向右预加载
  for (let i = 1; i <= cacheSize; i++) {
    const rightIndex = currentIndex + i;
    if (rightIndex < totalCount) {
      if (!skipCached || !cachedIndices.has(rightIndex)) {
        indices.push(rightIndex);
      }
    }
  }

  return {
    indices,
    direction: cacheSize > 1 ? 'both' : currentIndex > totalCount / 2 ? 'left' : 'right',
  };
}

// ==================== 淘汰算法 ====================

function calculateEvictionIndices(
  currentIndex: number,
  totalCount: number,
  maxCacheCount: number,
  cachedIndices: Set<number>
): number[] {
  if (cachedIndices.size <= maxCacheCount) {
    return [];
  }

  const toEvict: number[] = [];
  const needToEvict = cachedIndices.size - maxCacheCount;

  // 计算每个缓存项的距离（越远越优先淘汰）
  const distances = Array.from(cachedIndices).map((index) => ({
    index,
    distance: Math.abs(index - currentIndex),
  }));

  // 按距离排序，远的优先淘汰
  distances.sort((a, b) => b.distance - a.distance);

  // 淘汰最远的
  for (let i = 0; i < needToEvict && i < distances.length; i++) {
    toEvict.push(distances[i].index);
  }

  return toEvict;
}

// ==================== 默认骨架屏 ====================

const DefaultSkeleton = ({ count = 3 }: { count?: number }) => (
  <View style={styles.skeletonContainer}>
    {Array.from({ length: count }).map((_, index) => (
      <View key={`skeleton-${index}`} style={styles.skeletonItem}>
        <View style={styles.skeletonInner} />
      </View>
    ))}
  </View>
);

// ==================== 独立的 TabContent 组件 ====================

interface TabContentProps<T, TKey extends TabKey> {
  tabKey: TKey;
  dataCache: Map<TKey, TabDataCache<T>>;
  activeKey: TKey;
  renderItem: (item: T, index: number, tabKey: TKey) => React.ReactNode;
  keyExtractor: (item: T) => string;
  handleRefresh: () => Promise<void>;
  handleLoadMore: () => Promise<void>;
  emptyText: string;
  useSkeleton: boolean;
  skeletonComponent?: React.ReactNode;
  skeletonCount: number;
  emptyComponent?: React.ReactNode;
  listProps?: any;
}

const TabContent = React.memo(function TabContentComponent<T, TKey extends TabKey>({
  tabKey,
  dataCache,
  activeKey,
  renderItem,
  keyExtractor,
  handleRefresh,
  handleLoadMore,
  emptyText,
  useSkeleton,
  skeletonComponent,
  skeletonCount,
  emptyComponent,
  listProps,
}: TabContentProps<T, TKey>) {
  const cache = dataCache.get(tabKey) ?? {
    data: [],
    page: 0,
    hasMore: true,
    loadingState: 'idle' as const,
    initialized: false,
  };

  const isActive = tabKey === activeKey;

  // 使用 useCallback 保持 renderItem 引用稳定
  const renderListItem = useCallback((item: T, index: number) => {
    return renderItem(item, index, tabKey);
  }, [renderItem, tabKey]);

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
      <DbInfiniteList<T>
        data={cache.data}
        renderItem={renderListItem}
        keyExtractor={keyExtractor}
        onRefresh={isActive ? handleRefresh : undefined}
        onLoadMore={isActive ? handleLoadMore : undefined}
        refreshable={true}
        hasMore={cache.hasMore}
        loadingState={cache.loadingState}
        emptyText={emptyText}
        useSkeleton={useSkeleton}
        skeletonComponent={skeletonComponent ?? <DefaultSkeleton count={skeletonCount} />}
        emptyComponent={emptyComponent}
        contentContainerStyle={{ paddingTop: 8 }}
        // FlatList 性能优化配置
        initialNumToRender={5}
        maxToRenderPerBatch={5}
        windowSize={3}
        removeClippedSubviews={true}
        updateCellsBatchingPeriod={50}
        {...listProps}
      />
    </View>
  );
});

// ==================== 瀑布流 TabContent 组件 ====================

interface TabWaterfallContentProps<T, TKey extends TabKey> {
  tabKey: TKey;
  dataCache: Map<TKey, TabDataCache<T>>;
  activeKey: TKey;
  renderItem: (item: T, index: number, tabKey: TKey) => React.ReactNode;
  keyExtractor: (item: T) => string;
  handleLoadMore: () => Promise<void>;
  emptyText: string;
  useSkeleton: boolean;
  skeletonComponent?: React.ReactNode;
  skeletonCount: number;
  emptyComponent?: React.ReactNode;
  // 瀑布流配置
  numColumns: number;
  getItemHeight?: (item: T, index: number) => number;
  autoMeasureHeight?: boolean;
  defaultCardHeight?: number;
  itemSpacing: number;
  rowSpacing: number;
  sideSpacing: number;
  cardBorderRadius: number;
  cardShadow: boolean;
  cardShadowLevel: 'none' | 'sm' | 'md' | 'lg';
  // 骨架屏配置
  skeletonColumns?: number;
}

const TabWaterfallContent = React.memo(function TabWaterfallContentComponent<T, TKey extends TabKey>({
  tabKey,
  dataCache,
  activeKey,
  renderItem,
  keyExtractor,
  handleLoadMore,
  emptyText,
  useSkeleton,
  skeletonComponent,
  skeletonCount,
  emptyComponent,
  numColumns,
  getItemHeight,
  autoMeasureHeight,
  defaultCardHeight,
  itemSpacing,
  rowSpacing,
  sideSpacing,
  cardBorderRadius,
  cardShadow,
  cardShadowLevel,
  skeletonColumns,
}: TabWaterfallContentProps<T, TKey>) {
  const cache = dataCache.get(tabKey) ?? {
    data: [],
    page: 0,
    hasMore: true,
    loadingState: 'idle' as const,
    initialized: false,
  };

  const isActive = tabKey === activeKey;

  // 渲染瀑布流 item
  const renderWaterfallItem = useCallback((item: T, index: number) => {
    return renderItem(item, index, tabKey);
  }, [renderItem, tabKey]);

  // 计算实际的骨架屏列数
  const actualSkeletonColumns = skeletonColumns ?? numColumns;
  
  // 骨架屏组件 - 支持多列布局
  const WaterfallSkeleton = useMemo(() => {
    if (skeletonComponent) return skeletonComponent;
    
    // 将骨架项均匀分配到各列
    const itemsPerColumn = Math.ceil(skeletonCount / actualSkeletonColumns);
    const columns: React.ReactNode[] = [];
    
    for (let col = 0; col < actualSkeletonColumns; col++) {
      const columnItems: React.ReactNode[] = [];
      for (let row = 0; row < itemsPerColumn; row++) {
        const index = col * itemsPerColumn + row;
        if (index < skeletonCount) {
          columnItems.push(
            <View key={`skeleton-${index}`} style={[styles.skeletonItem, { marginBottom: rowSpacing }]}>
              <View style={styles.skeletonInner} />
            </View>
          );
        }
      }
      columns.push(
        <View
          key={`column-${col}`}
          style={[
            styles.skeletonColumn,
            { width: `${100 / actualSkeletonColumns}%`, paddingHorizontal: itemSpacing / 2 }
          ]}
        >
          {columnItems}
        </View>
      );
    }
    
    return (
      <View style={[styles.skeletonContainer, { paddingHorizontal: sideSpacing - itemSpacing / 2 }]}>
        {columns}
      </View>
    );
  }, [skeletonComponent, skeletonCount, actualSkeletonColumns, itemSpacing, rowSpacing, sideSpacing]);

  // 空状态
  if (cache.data.length === 0 && !cache.initialized && useSkeleton) {
    return (
      <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
        {WaterfallSkeleton}
      </View>
    );
  }

  // 真正的空状态
  if (cache.data.length === 0 && cache.initialized) {
    return (
      <View style={{ flex: 1, backgroundColor: '#f5f5f5', justifyContent: 'center', alignItems: 'center' }}>
        {emptyComponent || <View style={styles.emptyWrapper}><View style={styles.emptyText}>{emptyText}</View></View>}
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
      <DbItemCard.List
        data={cache.data}
        keyExtractor={keyExtractor}
        layoutMode="waterfall"
        numColumns={numColumns}
        itemSpacing={itemSpacing}
        rowSpacing={rowSpacing}
        sideSpacing={sideSpacing}
        borderRadius={cardBorderRadius}
        showShadow={cardShadow}
        shadowLevel={cardShadowLevel}
        cardBackgroundColor="#fff"
        showsScrollIndicator={true}
        scrollable={true}
        getItemHeight={getItemHeight}
        autoMeasureHeight={autoMeasureHeight}
        defaultCardHeight={defaultCardHeight}
        onItemPress={undefined}
        renderItem={renderWaterfallItem}
        onLoadMore={isActive ? handleLoadMore : undefined}
        isLoadingMore={cache.loadingState === 'loadingMore'}
        LoadingComponent={
          <View style={{ paddingVertical: 16, alignItems: 'center' }}>
            <View style={styles.loadingText}>加载中...</View>
          </View>
        }
        bottomSpacing={20}
      />
    </View>
  );
});

// ==================== 主组件 ====================

export const DbTabList = <T = any, TKey extends TabKey = string>({
  tabs,
  activeKey: controlledActiveKey,
  defaultActiveKey,
  initialPage,
  onChange,
  fetchData,
  renderItem,
  keyExtractor,
  pageSize = 10,
  cacheSize = 1,
  preload = true,
  maxCacheCount: propMaxCacheCount,
  useSkeleton = true,
  skeletonComponent,
  skeletonCount = 3,
  skeletonColumns,
  emptyComponent,
  emptyText = '暂无数据',
  // 布局类型
  layoutType = 'list',
  // 瀑布流配置
  numColumns = 2,
  getItemHeight,
  autoMeasureHeight,
  defaultCardHeight,
  itemSpacing = 8,
  rowSpacing = 10,
  sideSpacing = 12,
  cardBorderRadius = 12,
  cardShadow = true,
  cardShadowLevel = 'sm',
  tabProps,
  listProps,
  headerComponent,
  footerComponent,
  tabContentHeader: _tabContentHeader,
  style,
  tabBarStyle: _tabBarStyle,
  listContainerStyle: _listContainerStyle,
  renderTabBar,
  transformData,
  refreshTimeout = 15000,
}: DbTabListProps<T, TKey>) => {
  // 受控/非受控处理
  const isControlled = controlledActiveKey !== undefined;
  
  // 计算默认选中的 Tab key
  const getDefaultActiveKey = (): TKey | undefined => {
    // 优先使用 defaultActiveKey
    if (defaultActiveKey !== undefined) {
      return defaultActiveKey;
    }
    // 其次使用 initialPage
    if (initialPage !== undefined && tabs.length > 0) {
      const validIndex = Math.max(0, Math.min(initialPage, tabs.length - 1));
      return tabs[validIndex]?.key;
    }
    // 默认第一个
    return tabs[0]?.key;
  };
  
  const [internalActiveKey, setInternalActiveKey] = useState<TKey | undefined>(
    getDefaultActiveKey()
  );
  const activeKey = isControlled ? controlledActiveKey : (internalActiveKey ?? tabs[0]?.key);

  // 使用 LRU 缓存 - 优先使用传入的 maxCacheCount，否则自动计算
  const maxCacheCount = propMaxCacheCount ?? Math.max(cacheSize * 2 + 1, 3);
  const lruCacheRef = useRef(new LRUCache<TKey, TabDataCache<T>>(maxCacheCount));

  // 数据缓存状态（用于触发渲染）
  const [dataCache, setDataCache] = useState<Map<TKey, TabDataCache<T>>>(new Map());

  // 正在加载的 Tab 集合（防止重复加载）
  const loadingTabsRef = useRef<Set<TKey>>(new Set());

  // 获取当前 Tab 索引
  const currentIndex = useMemo(() => {
    return tabs.findIndex((tab) => tab.key === activeKey);
  }, [tabs, activeKey]);

  // 使用 ref 存储 updateCache，避免循环依赖
  const updateCacheRef = useRef((tabKey: TKey, updates: Partial<TabDataCache<T>>) => {
    const existing = lruCacheRef.current.get(tabKey) ?? {
      data: [],
      page: 0,
      hasMore: true,
      loadingState: 'idle' as const,
      initialized: false,
    };
    const newCacheData = { ...existing, ...updates };

    // 更新 LRU 缓存
    lruCacheRef.current.set(tabKey, newCacheData);

    // 更新 React state（触发渲染）
    setDataCache((prev) => {
      // 只有数据真正变化时才更新
      const prevData = prev.get(tabKey);
      if (prevData &&
          prevData.data === newCacheData.data &&
          prevData.loadingState === newCacheData.loadingState &&
          prevData.hasMore === newCacheData.hasMore &&
          prevData.page === newCacheData.page) {
        return prev;
      }
      const newMap = new Map(prev);
      newMap.set(tabKey, newCacheData);
      return newMap;
    });
  });
  

  // 加载指定 Tab 的数据
  const loadData = useCallback(
    async (tabKey: TKey, isRefresh = false) => {
      // 防止重复加载
      if (loadingTabsRef.current.has(tabKey)) return;

      const cache = lruCacheRef.current.get(tabKey);
      const isFirstLoad = !cache?.initialized;
      const currentPage = isRefresh ? 1 : (cache?.page ?? 0) + 1;

      loadingTabsRef.current.add(tabKey);
      updateCacheRef.current(tabKey, {
        loadingState: isFirstLoad ? 'loading' : isRefresh ? 'refreshing' : 'loadingMore',
      });

      try {
        const result = await fetchData({
          tabKey,
          page: currentPage,
          pageSize,
        });

        const transformedData = transformData ? transformData(result.data) : result.data;
        const newData = isRefresh ? transformedData : [...(cache?.data ?? []), ...transformedData];

        updateCacheRef.current(tabKey, {
          data: newData,
          page: currentPage,
          hasMore: result.hasMore,
          loadingState: result.hasMore ? 'idle' : 'noMore',
          initialized: true,
          total: result.total,
        });
      } catch (error) {
        updateCacheRef.current(tabKey, {
          loadingState: 'error',
        });
      } finally {
        loadingTabsRef.current.delete(tabKey);
      }
    },
    [fetchData, pageSize, transformData]
  );

  // 刷新当前 Tab（带超时机制）
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleRefresh = useCallback(async () => {
    // 清除之前的超时定时器
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // 设置超时定时器
    refreshTimeoutRef.current = setTimeout(() => {
      // 超时后强制重置刷新状态
      loadingTabsRef.current.delete(activeKey);
      updateCacheRef.current(activeKey, {
        loadingState: 'error',
      });
      refreshTimeoutRef.current = null;
    }, refreshTimeout);
    
    try {
      await loadData(activeKey, true);
    } finally {
      // 清除超时定时器
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      // 延迟重置 loadingState，确保 RefreshControl 有足够时间收起动画
      const cache = lruCacheRef.current.get(activeKey);
      if (cache?.loadingState === 'refreshing') {
        setTimeout(() => {
          updateCacheRef.current(activeKey, {
            loadingState: 'idle',
          });
        }, 300);
      }
    }
  }, [loadData, activeKey, refreshTimeout]);

  // 清理超时定时器
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // 加载更多
  const handleLoadMore = useCallback(async () => {
    const cache = lruCacheRef.current.get(activeKey);
    if (!cache?.hasMore || cache.loadingState === 'loadingMore') return;
    await loadData(activeKey, false);
  }, [loadData, activeKey]);

  // Tab 切换
  const handleTabChange = useCallback((index: number, tab: any) => {
    const newKey = tab.tabData?.key as TKey;
    if (!isControlled) {
      setInternalActiveKey(newKey);
    }
    onChange?.(newKey);
  }, [isControlled, onChange]);

  // 监听 initialPage 变化，更新 activeKey
  // 使用 ref 避免循环依赖
  const internalActiveKeyRef = useRef(internalActiveKey);
  internalActiveKeyRef.current = internalActiveKey;
  
  useEffect(() => {
    if (initialPage !== undefined && tabs.length > 0 && !isControlled) {
      const validIndex = Math.max(0, Math.min(initialPage, tabs.length - 1));
      const newKey = tabs[validIndex]?.key;
      if (newKey && newKey !== internalActiveKeyRef.current) {
        setInternalActiveKey(newKey);
      }
    }
  }, [initialPage, tabs, isControlled]);

  // LRU 淘汰机制：使用防抖减少频繁操作
  const evictionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // 清除之前的定时器
    if (evictionTimeoutRef.current) {
      clearTimeout(evictionTimeoutRef.current);
    }
    
    // 延迟执行淘汰，避免快速滑动时频繁操作
    evictionTimeoutRef.current = setTimeout(() => {
      const cachedIndices = new Set<number>();
      const keys = lruCacheRef.current.keys();
      for (const key of keys) {
        const index = tabs.findIndex((tab) => tab.key === key);
        if (index !== -1) {
          cachedIndices.add(index);
        }
      }

      const toEvict = calculateEvictionIndices(
        currentIndex,
        tabs.length,
        maxCacheCount,
        cachedIndices
      );

      // 只有当需要淘汰时才更新 state
      if (toEvict.length > 0) {
        // 执行淘汰
        toEvict.forEach((index) => {
          const key = tabs[index]?.key;
          if (key !== undefined) {
            lruCacheRef.current.delete(key);
          }
        });
        
        // 批量更新 state
        setDataCache((prev) => {
          const newMap = new Map(prev);
          toEvict.forEach((index) => {
            const key = tabs[index]?.key;
            if (key !== undefined) {
              newMap.delete(key);
            }
          });
          return newMap;
        });
      }
    }, 500); // 500ms 防抖

    return () => {
      if (evictionTimeoutRef.current) {
        clearTimeout(evictionTimeoutRef.current);
      }
    };
  }, [currentIndex, tabs, maxCacheCount]);

  // 使用 ref 存储 loadData，避免 useEffect 依赖循环
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  // 初始加载当前 Tab
  useEffect(() => {
    const cache = lruCacheRef.current.get(activeKey);
    if (!cache?.initialized) {
      loadDataRef.current(activeKey, true);
    }
  }, [activeKey]);

  // 预加载相邻 Tab - 降低频率
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const preloadTimeoutsRef = useRef<NodeJS.Timeout[]>([]);
  
  useEffect(() => {
    if (!preload || tabs.length <= 1 || cacheSize <= 0) return;

    // 清除之前的预加载定时器
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }

    // 延迟预加载，避免快速滑动时频繁请求
    preloadTimeoutRef.current = setTimeout(() => {
      // 构建已缓存的索引集合
      const cachedIndices = new Set<number>();
      const keys = lruCacheRef.current.keys();
      for (const key of keys) {
        const index = tabs.findIndex((tab) => tab.key === key);
        if (index !== -1) {
          cachedIndices.add(index);
        }
      }

      // 计算需要预加载的索引
      const { indices } = calculatePreloadIndices({
        currentIndex,
        totalCount: tabs.length,
        cacheSize,
        skipCached: true,
        cachedIndices,
      });

      // 串行预加载，避免并发请求过多
      // 使用 ref 存储 timeouts，确保能正确清理
      preloadTimeoutsRef.current.forEach(clearTimeout);
      preloadTimeoutsRef.current = [];
      
      indices.forEach((index, i) => {
        const tabKey = tabs[index]?.key;
        if (tabKey !== undefined) {
          const timeout = setTimeout(() => {
            const cache = lruCacheRef.current.get(tabKey);
            if (!cache?.initialized) {
              loadDataRef.current(tabKey, true);
            }
          }, i * 300); // 增加间隔到 300ms
          preloadTimeoutsRef.current.push(timeout);
        }
      });
    }, 300); // 延迟 300ms 再开始预加载

    return () => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
      // 清理所有预加载的 timeouts
      preloadTimeoutsRef.current.forEach(clearTimeout);
      preloadTimeoutsRef.current = [];
    };
  }, [activeKey, tabs, cacheSize, preload, currentIndex]);

  // 组件卸载时清理所有定时器
  useEffect(() => {
    return () => {
      if (evictionTimeoutRef.current) {
        clearTimeout(evictionTimeoutRef.current);
      }
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
      preloadTimeoutsRef.current.forEach(clearTimeout);
      preloadTimeoutsRef.current = [];
    };
  }, []);

  // 转换 tabs 格式给 DbTab
  const tabItems = useMemo(() => {
    return tabs.map((tab) => ({
      key: String(tab.key),
      title: tab.title,
      tabData: tab,
    }));
  }, [tabs]);

  // 准备 TabContent 的 props
  const tabContentProps = useMemo(() => ({
    dataCache,
    activeKey,
    renderItem,
    keyExtractor,
    handleRefresh,
    handleLoadMore,
    emptyText,
    useSkeleton,
    skeletonComponent,
    skeletonCount,
    emptyComponent,
    listProps,
  }), [dataCache, activeKey, renderItem, keyExtractor, handleRefresh, handleLoadMore, emptyText, useSkeleton, skeletonComponent, skeletonCount, emptyComponent, listProps]);

  // 准备瀑布流 TabContent 的 props
  const waterfallTabContentProps = useMemo(() => ({
    dataCache,
    activeKey,
    renderItem,
    keyExtractor,
    handleLoadMore,
    emptyText,
    useSkeleton,
    skeletonComponent,
    skeletonCount,
    skeletonColumns,
    emptyComponent,
    numColumns,
    getItemHeight,
    autoMeasureHeight,
    defaultCardHeight,
    itemSpacing,
    rowSpacing,
    sideSpacing,
    cardBorderRadius,
    cardShadow,
    cardShadowLevel,
  }), [dataCache, activeKey, renderItem, keyExtractor, handleLoadMore, emptyText, useSkeleton, skeletonComponent, skeletonCount, skeletonColumns, emptyComponent, numColumns, getItemHeight, autoMeasureHeight, defaultCardHeight, itemSpacing, rowSpacing, sideSpacing, cardBorderRadius, cardShadow, cardShadowLevel]);

  // renderContent 使用稳定的引用
  const renderContent = useCallback((index: number, item: any) => {
    const tab = item.tabData as TabItem<TKey>;
    
    // 根据 layoutType 选择渲染方式
    if (layoutType === 'waterfall') {
      return <TabWaterfallContent {...waterfallTabContentProps} tabKey={tab.key} />;
    }
    
    return <TabContent {...tabContentProps} tabKey={tab.key} />;
  }, [layoutType, tabContentProps, waterfallTabContentProps]);

  // 渲染 TabBar
  const renderTabBarComponent = useCallback((props: any) => {
    if (renderTabBar) {
      return renderTabBar(props);
    }
    return null;
  }, [renderTabBar]);

  // 如果 tabs 为空，显示空状态
  if (!tabs || tabs.length === 0) {
    return (
      <View style={[styles.container, style]}>
        <View style={styles.emptyContainer}>
          {/* 可以在这里添加空状态图标 */}
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      {headerComponent}
      <DbTab
        tabs={tabItems}
        renderContent={renderContent}
        initialPage={currentIndex >= 0 ? currentIndex : 0}
        onTabChange={handleTabChange}
        renderTabBar={renderTabBar ? renderTabBarComponent : undefined}
        {...tabProps}
      />
      {footerComponent}
    </View>
  );
};

// ==================== 样式 ====================

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  skeletonContainer: {
    flexDirection: 'row',
    paddingVertical: 12,
  },
  skeletonColumn: {
    flexDirection: 'column',
  },
  skeletonItem: {
    backgroundColor: '#fff',
    padding: 12,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  skeletonInner: {
    height: 120,
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  emptyWrapper: {
    padding: 20,
  },
  emptyText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: '#999',
  },
});

export default DbTabList;

// 导出类型
export type {
  DbTabListProps,
  TabDataCache,
  TabKey,
  TabItem,
  FetchDataParams,
  FetchDataResult,
  LayoutType,
} from './DbTabList.types';
